import { useState } from "react";
import { useParams } from "react-router-dom";
import type { ZodTypeAny } from "zod";
import { orderCategories } from "../registry/categories";
import { getCore, listCategories, listCores } from "../registry/toolRegistry";
import { defaultsOf } from "../settings/introspect";
import { MediaPanel } from "../shell/MediaPanel";
import { RightSidebar } from "../shell/RightSidebar";
import { SettingsPanel } from "../shell/SettingsPanel";
import { ToolTabs } from "../shell/ToolTabs";
import { useOpenedJob } from "../shell/useOpenedJob";
import { useToolUI } from "../shell/useToolUI";
import { useUiStore } from "../state/uiStore";
import { Button, Icon, Spinner, cx } from "../ui";

// §11 Studio — die zentrale Generierungs-Fläche und ZUHAUSE der „4-Regionen-Arbeitsfläche"
// aus §11 (NICHT in shell/AppShell.tsx, das nur Rail + <Outlet> rendert). Die vier Regionen:
//   (A) ToolTabs · (B) MediaPanel · (C) SettingsPanel · (D) RightSidebar.
// Liest Kategorie/Tool aus der URL, fällt sonst auf das erste registrierte Tool zurück (kein
// hartes Default-Tool, §4). Region D ist ab xl permanent, sonst ein Drawer. Desktop-first (§11).

export function Studio() {
  const params = useParams<{ category?: string; toolId?: string }>();
  const [drawer, setDrawer] = useState(false);

  const categories = orderCategories(listCategories());
  const category = params.category && listCores(params.category).length ? params.category : (categories[0] ?? "");
  const tools = listCores(category);
  const toolId = params.toolId && tools.some((t) => t.id === params.toolId) ? params.toolId : tools[0]?.id;
  const core = toolId ? getCore(toolId) : undefined;
  const { ui, loading } = useToolUI(toolId);
  // §11/D65 — geöffneten Job rehydrieren (settings/inputs als Seiteneffekt) + Output liefern.
  // Unkonditional VOR dem early-return (Rules of Hooks); `core?.id` darf undefined sein.
  const { outputs, resultUrls, signals, jobStatus } = useOpenedJob(core?.id);

  if (!core) {
    return <div className="grid h-full place-items-center text-sm text-muted">Kein Tool registriert.</div>;
  }

  // „Neuer Task": geöffneten Job deselektieren (kein Rehydrieren mehr, useOpenedJob.matches→false)
  // und die Felder freigeben — Draft auf die Schema-Defaults (SSOT: defaultsOf, wie SettingsPanel-
  // Seed) und hochgeladene Inputs leeren (gibt Object-URLs frei). Über getState() statt Selektoren,
  // da reine Imperativ-Aktion. closeJob ZUERST: der Live-Guard in useOpenedJob liest den Store, damit
  // ein noch ladendes Detail den frischen Draft nicht überschreibt (D123/Codex-P1).
  const handleNewTask = () => {
    const store = useUiStore.getState();
    store.closeJob();
    store.setDraft(core.id, defaultsOf(core.settingsSchema as unknown as ZodTypeAny));
    store.clearInputs(core.id);
  };

  return (
    // `grid-rows-[minmax(0,1fr)]` FIXIERT die einzige Zeile auf die Grid-(Viewport-)Höhe. Ohne das ist die
    // implizite Zeile `auto` und wird vom HÖHEREN Spalten-Inhalt aufgebläht — konkret von der rechten Jobs-
    // Sidebar (lange Liste): dann wächst auch die Hauptspalte auf die Listenhöhe, ihr `overflow-y-auto`
    // greift nicht mehr (kein interner Überlauf) und das ganze Grid läuft unter dem `overflow-hidden`-main
    // hinaus → NICHTS scrollt mehr. Mit fixer 1fr-Zeile bekommen beide Spalten eine definite Höhe und
    // scrollen je intern (Hauptspalte hier, Sidebar via RightSidebars eigenem `h-full overflow-y-auto`).
    <div className="grid h-full min-h-0 grid-cols-1 grid-rows-[minmax(0,1fr)] xl:grid-cols-[minmax(0,1fr)_320px]">
      {/* Hauptspalte = DER Scroll-Container (overflow-y-auto, eine einzige Scroll-Ebene). Sie hat dank der
          fixen 1fr-Grid-Zeile eine feste Höhe (Viewport); ihr Inhalt (Tabs + Grid) fließt NATÜRLICH und
          scrollt als Ganzes, sobald er überläuft — UNABHÄNGIG von der Höhe der rechten Sidebar. */}
      <div className="flex min-h-0 min-w-0 flex-col overflow-y-auto">
        {/* Tab-Leiste bleibt beim Scrollen oben (sticky, eigener bg gegen Durchscheinen). */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-border bg-bg">
          <ToolTabs category={category} activeToolId={core.id} />
          <div className="flex shrink-0 items-center gap-1 pr-2">
            <Button variant="ghost" size="sm" icon="plus" onClick={handleNewTask}>
              Neuer Task
            </Button>
            <Button variant="ghost" size="sm" icon="folder" className="xl:hidden" onClick={() => setDrawer(true)}>
              Ordner
            </Button>
          </div>
        </div>

        {/* Inhalts-Grid mit NATÜRLICHER Höhe (kein flex-1/min-h-0/overflow): `content-start`/
            `items-start` packen die Spalten oben (kein gestreckter Leerraum, Generate-Leiste folgt
            dem Inhalt). Der Scroll besorgt die Hauptspalte oben. */}
        <div className="grid grid-cols-1 content-start items-start gap-4 p-4 lg:grid-cols-[minmax(0,1fr)_360px]">
          <MediaPanel core={core} ui={ui} result={outputs} resultUrls={resultUrls} signals={signals} jobStatus={jobStatus} />
          <div className="panel">
            {loading ? (
              <div className="grid min-h-[200px] place-items-center p-8">
                <Spinner />
              </div>
            ) : (
              <SettingsPanel core={core} ui={ui} />
            )}
          </div>
        </div>
      </div>

      {/* Rechte Region: xl permanent, sonst Drawer */}
      <aside className="hidden border-l border-border bg-surface xl:block">
        <RightSidebar />
      </aside>

      {drawer && (
        <div className="fixed inset-0 z-40 xl:hidden" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDrawer(false)} />
          {/* Drawer = flex-col: Header `shrink-0`, RightSidebar im `flex-1 min-h-0`-Rest → die
              Sidebar (h-full + overflow-y-auto) füllt NUR Drawer−Header (sonst rutscht der untere
              Teil um die Header-Höhe unter den Viewport, Codex-P1). */}
          <div className={cx("absolute right-0 top-0 flex h-full w-[320px] max-w-[85vw] flex-col border-l border-border bg-surface")}>
            <div className="flex shrink-0 items-center justify-between border-b border-border px-3 py-2">
              <span className="text-sm font-semibold">Ordner & Jobs</span>
              <button type="button" onClick={() => setDrawer(false)} className="text-muted hover:text-text">
                <Icon name="x" size={18} />
              </button>
            </div>
            <div className="min-h-0 flex-1">
              <RightSidebar />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
